import { apiClient } from './api';

// NOTE: This is a SEPARATE type from integrationsApi.ts's `ImportJob` (which is
// the email-provider import job with a different status set + `provider`). Do not
// merge them — the email-import StatusBadge label map is typed against that union.

export type CrmImportStatus =
  | 'uploaded'
  | 'mapping'
  | 'confirmed'
  | 'running'
  | 'done'
  | 'failed'
  | 'cancelled';

export interface CrmImportSuggestion {
  field: string | null;            // a SuiteCRM field, '__account__', or null (skip)
  confidence: number;
  is_enum: boolean;
  enum_options?: Record<string, string>;
  value_validation?: Record<string, string | null>;  // source value -> matched key | null
}

export interface CrmImportSheet {
  sheet_id: number;
  sheet_name: string;
  sheet_index: number;
  headers: string[];
  sample_rows: Record<string, any>[];
  row_count: number;
  target_module: string | null;     // 'Leads' | 'Contacts' | 'Accounts' | null
  suggested_mapping: Record<string, CrmImportSuggestion> | null;
  confirmed_mapping: Record<string, string | null> | null;
  account_link: { strategy: string; column?: string; account_sheet_id?: number } | null;
  // google-sheets-lead-import Phase 2
  static_values: Record<string, string> | null;   // per-sheet constants, e.g. { lead_source: 'Campaign' }
  detected_preset: string | null;                 // 'meta_lead_ads' | null — recomputed from headers
  lead_source_options?: Record<string, string>;   // suggest response only (live CRM dropdown)
}

export interface CrmImportError {
  sheet: string | null;
  row_index: number | null;
  email: string | null;
  error: string;
}

export interface CrmImportJob {
  id: number;
  status: CrmImportStatus;
  source_kind: string;              // 'file' | 'gsheet'
  source_ref: string | null;        // gsheet: 'gsheet:<spreadsheet_id>#gid=<gid>'
  original_filename: string | null;
  file_format: string | null;
  tier_limit: number | null;
  partial_over_cap: boolean;
  skipped_over_cap: number;
  total_rows: number;
  records_processed: number;
  records_created: number;
  records_updated: number;
  records_failed: number;
  accounts_created: number;
  error_log: CrmImportError[];
  campaign_id: string | null;
  campaign_name: string | null;
  // google-sheets-lead-import Phase 3a: set on a job that belongs to a syncing
  // Google Sheet source (a /remap job, or a sync-confirmed / sync-run job).
  gsheet_source_id: number | null;
  enrollments_created: number;
  enrollments_failed: number;
  started_at: string | null;
  completed_at: string | null;
  created_at: string | null;
  sheets: CrmImportSheet[];
}

export interface CrmImportCampaign {
  campaign_id: string;
  name: string;
  project_id: string;
  project_name: string | null;
  status: string | null;
}

export interface SheetTarget {
  sheet_id: number;
  target_module: string | null;
  account_link?: { strategy: string; column?: string; account_sheet_id?: number } | null;
}

export interface ConfirmSheet {
  sheet_id: number;
  target_module: string | null;
  confirmed_mapping: Record<string, string | null>;
  account_link?: { strategy: string; column?: string; account_sheet_id?: number } | null;
  // Omit to keep the server-held value seeded by suggest.
  static_values?: Record<string, string>;
}

// Mapping sentinels (mirrors crm_mapping_suggester.py). Never literal CRM fields.
export const ACCOUNT_SENTINEL = '__account__';
export const FULL_NAME_SENTINEL = '__full_name__';
export const ATTRIBUTION_SENTINEL = '__attribution__';

// "Keep importing new rows" options for a Google Sheet confirm (Phase 3a).
// The key strategy is explicit; the request always carries all three key fields.
export type GsheetKeyStrategy = 'column' | 'email_created' | 'row_hash';
export interface GsheetSyncOptions {
  enabled: boolean;
  interval_minutes: number;          // 15..1440
  key_strategy: GsheetKeyStrategy;
  key_column: string | null;         // required for 'column'
  key_time_column: string | null;    // required for 'email_created'; else the blank-key fallback
  baseline_existing?: boolean;       // Phase 3b — rejected until then
}

export interface ConfirmResponse {
  job_id: number;
  queued: boolean;                   // false: a sync confirm with nothing new to import
  key_conflicts?: number;
}

// The 409 over_cap body the confirm endpoint returns when a file exceeds the cap.
export interface OverCapDetail {
  error: 'over_cap';
  limit: number;
  total_rows: number;
  upgrade_cta: boolean;
}

export const crmImportApi = {
  upload: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiClient.post<CrmImportJob>('/crm-import/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  suggest: (jobId: number, sheetTargets: SheetTarget[]) =>
    apiClient.post<CrmImportJob>(`/crm-import/jobs/${jobId}/suggest`, {
      sheet_targets: sheetTargets,
    }, {
      // Per-sheet enum lookups + Gemini mapping run sequentially server-side and
      // can exceed the default 120s. Match nginx's /api proxy_read_timeout (300s)
      // so the browser doesn't abort and cancel the in-progress request. The
      // backend also commits per-sheet, so a timeout no longer loses progress.
      timeout: 300000,
    }),
  confirm: (
    jobId: number,
    sheets: ConfirmSheet[],
    allowPartial = false,
    campaignId?: string | null,
    gsheetSync?: GsheetSyncOptions,
    projectId?: string | null,
  ) =>
    apiClient.post<ConfirmResponse>(`/crm-import/jobs/${jobId}/confirm`, {
      sheets,
      allow_partial: allowPartial,
      campaign_id: campaignId ?? null,
      project_id: projectId ?? null,
      ...(gsheetSync ? { gsheet_sync: gsheetSync } : {}),
    }),
  listJobs: () => apiClient.get<CrmImportJob[]>('/crm-import/jobs'),
  getJob: (jobId: number) => apiClient.get<CrmImportJob>(`/crm-import/jobs/${jobId}`),
  errorsCsvUrl: (jobId: number) => `/crm-import/jobs/${jobId}/errors.csv`,
  downloadErrors: (jobId: number) =>
    apiClient.get<string>(`/crm-import/jobs/${jobId}/errors.csv`, { responseType: 'text' as any }),
  // GET /api/crm-import/campaigns — deliberately re-exposed under the
  // `integrations` gate (not campaignsApi's `campaigns`-gated list) so the
  // picker never needs `campaigns` module access. See CLAUDE.md's Ad Copy
  // dual-gate note and campaign-sequence-enrollment.md Phase 5.
  listCampaigns: (projectId?: string | null) =>
    apiClient.get<CrmImportCampaign[]>('/crm-import/campaigns', {
      params: projectId ? { project_id: projectId } : undefined,
    }),
};

// Curated field options for the mapping dropdowns, mirrored from the backend
// FIELD_CATALOGUES so the UI can render a <select> per column without an extra
// round-trip. Keep in sync with crm_mapping_suggester.py.
export const CRM_FIELD_OPTIONS: Record<string, { field: string; label: string; required?: boolean }[]> = {
  Leads: [
    { field: 'salutation', label: 'Salutation' },
    { field: 'first_name', label: 'First Name' },
    { field: 'last_name', label: 'Last Name', required: true },
    { field: 'title', label: 'Job Title' },
    { field: 'email1', label: 'Email' },
    { field: 'phone_work', label: 'Office Phone' },
    { field: 'phone_mobile', label: 'Mobile' },
    { field: 'phone_home', label: 'Home Phone' },
    { field: 'phone_fax', label: 'Fax' },
    { field: 'lead_source', label: 'Lead Source' },
    { field: 'lead_source_description', label: 'Lead Source Description' },
    { field: 'status', label: 'Status' },
    { field: 'description', label: 'Description' },
    { field: 'website', label: 'Website' },
    { field: 'primary_address_street', label: 'Street' },
    { field: 'primary_address_city', label: 'City' },
    { field: 'primary_address_state', label: 'State' },
    { field: 'primary_address_postalcode', label: 'Postcode' },
    { field: 'primary_address_country', label: 'Country' },
    { field: '__account__', label: 'Company / Account name' },
    { field: '__full_name__', label: 'Full name (split into first/last)' },
    { field: '__attribution__', label: 'Lead attribution (campaign/ad/form)' },
  ],
  Contacts: [
    { field: 'salutation', label: 'Salutation' },
    { field: 'first_name', label: 'First Name' },
    { field: 'last_name', label: 'Last Name', required: true },
    { field: 'title', label: 'Job Title' },
    { field: 'department', label: 'Department' },
    { field: 'email1', label: 'Email' },
    { field: 'phone_work', label: 'Office Phone' },
    { field: 'phone_mobile', label: 'Mobile' },
    { field: 'phone_home', label: 'Home Phone' },
    { field: 'phone_fax', label: 'Fax' },
    { field: 'lead_source', label: 'Lead Source' },
    { field: 'description', label: 'Description' },
    { field: 'birthdate', label: 'Birthdate' },
    { field: 'primary_address_street', label: 'Street' },
    { field: 'primary_address_city', label: 'City' },
    { field: 'primary_address_state', label: 'State' },
    { field: 'primary_address_postalcode', label: 'Postcode' },
    { field: 'primary_address_country', label: 'Country' },
    { field: '__account__', label: 'Company / Account name' },
    { field: '__full_name__', label: 'Full name (split into first/last)' },
    { field: '__attribution__', label: 'Lead attribution (campaign/ad/form)' },
  ],
  Accounts: [
    { field: 'name', label: 'Account Name', required: true },
    { field: 'email1', label: 'Email' },
    { field: 'phone_office', label: 'Office Phone' },
    { field: 'phone_fax', label: 'Fax' },
    { field: 'website', label: 'Website' },
    { field: 'industry', label: 'Industry' },
    { field: 'account_type', label: 'Type' },
    { field: 'annual_revenue', label: 'Annual Revenue' },
    { field: 'employees', label: 'Employees' },
    { field: 'description', label: 'Description' },
    { field: 'billing_address_street', label: 'Billing Street' },
    { field: 'billing_address_city', label: 'Billing City' },
    { field: 'billing_address_state', label: 'Billing State' },
    { field: 'billing_address_postalcode', label: 'Billing Postcode' },
    { field: 'billing_address_country', label: 'Billing Country' },
  ],
};

export const CRM_IMPORT_MODULES = ['Leads', 'Contacts', 'Accounts'] as const;
